import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  DarkMode,
  Flex,
  Grid,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Avatar } from '../Avatar';
import {
  ChevronDown,
  Mic,
  MicOff,
  Phone,
  PictureInPicture2,
  SwitchCamera,
  UserPlus,
  Video as VideoIcon,
  VideoOff,
  WifiOff,
  X,
} from 'lucide-react';
import { getDefaultStore, useAtom, useAtomValue } from 'jotai';
import {
  activeCallInfoAtom,
  callMinimizedAtom,
  currentFamilyIdAtom,
  currentUserAtom,
} from '../../store';
import { useCall } from '../../hooks/useCall';
import { useFamily } from '../../hooks/useFamily';
import { useSpeaking } from '../../hooks/useSpeaking';
import { useCallPip, type PipMode } from '../../hooks/useCallPip';
import { holdBackForCall } from '../../lib/callHistory';
import { pickMainRemote } from '../../lib/callTile';
import { api } from '../../lib/api';
import { LogoMark } from '../brand/Logo';
import { t as tNow, useT } from '../../i18n';
import { CallAudio, StreamVideo, videoForStream } from './callMedia';
import { CallPipView, CallTile, type MiniCallProps } from './CallTile';

// Call screens are always dark, whatever the app's mode (Living Room calls).
const CALL_BG = '#171513';
const VIDEO_BG = '#0F0E0D';
const TILE_BG = '#211E1B';
const CONTROL_BG = '#2A2724';
const DECLINE = '#C62828';
const ANSWER = '#1A7A43';
const SAFFRON = '#F0B63F';

function HangUpIcon({ size }: { size: number }) {
  return (
    <Phone
      size={size}
      style={{ transform: 'rotate(135deg)' }}
      aria-hidden="true"
    />
  );
}

// "12:48", or "1:02:09" for long calls.
export function formatCallTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${s}`
    : `${String(m).padStart(2, '0')}:${s}`;
}

/** Re-renders every second while a call is connected. */
export function useCallClock(startedAt: number | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt === null) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [startedAt]);
  return startedAt === null ? '00:00' : formatCallTime(now - startedAt);
}

// A round control with its label underneath (voice call).
function RoundControl({
  label,
  caption,
  icon,
  active,
  onClick,
}: {
  label: string;
  caption: string;
  icon: React.ReactElement;
  /** Light fill when switched on (design: Speaker on, Mute on). */
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <VStack spacing="6px">
      <IconButton
        colorScheme="gray"
        aria-label={label}
        aria-pressed={active}
        icon={icon}
        w="68px"
        h="68px"
        bg={active ? '#F3F0EB' : CONTROL_BG}
        color={active ? CALL_BG : '#F3F0EB'}
        _hover={{ bg: active ? '#FFFFFF' : '#34302C' }}
        _active={{ bg: active ? '#FFFFFF' : '#34302C' }}
        onClick={onClick}
      />
      <Text fontSize="14px" fontWeight={600}>
        {caption}
      </Text>
    </VStack>
  );
}

export function CallOverlay() {
  const {
    state,
    notice,
    error,
    acceptCall,
    rejectCall,
    endCall,
    toggleMic,
    toggleCamera,
    ringMore,
    flipCamera,
    resumeVideo,
    dismissVideoHint,
    dismissNotice,
  } = useCall();
  const addPicker = useDisclosure();
  const currentUser = useAtomValue(currentUserAtom);
  const { currentFamily } = useFamily();
  const toast = useToast();
  const t = useT();
  const [minimized, setMinimized] = useAtom(callMinimizedAtom);
  const [info, setInfo] = useAtom(activeCallInfoAtom);
  const [participantNames, setParticipantNames] = useState<
    Record<string, { name: string; avatarUrl: string | null }>
  >({});
  const [isGroupCall, setIsGroupCall] = useState(false);
  // The call's chat's family: faces open through it (D292).
  const [callFamilyId, setCallFamilyId] = useState<string | null>(null);
  const clock = useCallClock(info.startedAt);

  // Surface ephemeral call notices (missed, declined, permission errors) as toasts.
  useEffect(() => {
    const message = error ?? notice;
    if (!message) return;
    toast({
      title: message,
      status: error ? 'error' : 'info',
      duration: 5000,
      isClosable: true,
    });
    dismissNotice();
  }, [error, notice, toast, dismissNotice]);

  // The chat's name ("Everyone", "Sunday lunch crew", or the other person),
  // and names for anyone in it who isn't in the current family's list.
  const conversationId = state.conversationId;
  useEffect(() => {
    setCallFamilyId(null);
    if (!conversationId) return;
    let cancelled = false;
    api
      .getConversation(conversationId)
      .then(({ data }) => {
        if (cancelled) return;
        const others = data.participants.filter(
          (p) => p.id !== currentUser?.id
        );
        const title = data.isDefault
          ? tNow('chat.header.everyone')
          : (data.name ??
            (others.map((p) => p.displayName).join(', ') ||
              tNow('chat.callScreen.call')));
        setInfo((current) => ({ ...current, title }));
        setCallFamilyId(data.familyId);
        setIsGroupCall(data.participants.length > 2);
        setParticipantNames(
          Object.fromEntries(
            data.participants.map((p) => [
              p.id,
              { name: p.displayName, avatarUrl: p.avatarUrl },
            ])
          )
        );
      })
      .catch(() => {
        // Not found or offline: the family on screen is the call's best
        // guess, so faces still open through a family (D292).
        if (!cancelled)
          setCallFamilyId(getDefaultStore().get(currentFamilyIdAtom));
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, currentUser?.id, setInfo]);

  // The clock starts when the call connects; everything resets when it ends.
  const status = state.status;
  useEffect(() => {
    if (status === 'active') {
      setInfo((current) =>
        current.startedAt === null
          ? { ...current, startedAt: Date.now() }
          : current
      );
    } else {
      // Every call starts on the full screen (D267).
      setMinimized(false);
      if (status === 'idle') {
        setInfo({ title: '', startedAt: null });
        setParticipantNames({});
      }
    }
  }, [status, setInfo, setMinimized]);
  // Read by PiP's leave handler, which can fire after the call has ended.
  const statusRef = useRef(status);
  statusRef.current = status;

  const isSelf = (id: string) => id === currentUser?.id;
  const remoteIds = state.participants.filter((id) => !isSelf(id));
  // People in this chat who aren't in the call, who can be rung (D38).
  const addable = Object.keys(participantNames).filter(
    (id) => !isSelf(id) && !state.participants.includes(id)
  );
  const remoteMuted = (id: string) =>
    state.remoteMedia[id]?.micEnabled === false;
  const remoteCameraOff = (id: string) =>
    state.remoteMedia[id]?.cameraEnabled === false;
  const isReconnecting = (id: string) => state.reconnecting.includes(id);
  const speaking = useSpeaking({
    ...state.remoteStreams,
    ...(currentUser && state.localStream && state.micEnabled
      ? { [currentUser.id]: state.localStream }
      : {}),
  });

  // ---- Minimise and picture-in-picture (M70, D267–D268) -----------------
  const active = state.status === 'active';
  const video = state.type === 'video';
  const showsVideo = (id: string) => {
    const stream = state.remoteStreams[id];
    return (
      !!stream &&
      !remoteCameraOff(id) &&
      stream.getVideoTracks().some((t) => t.enabled && t.readyState === 'live')
    );
  };
  // The tile and PiP show one person: whoever talks, else who was shown.
  // While their video is in the browser's PiP window it must stay theirs
  // and stay in the page — taking the element out closes PiP — so nobody
  // else talking, or their camera going off, changes it then.
  const pipModeRef = useRef<PipMode>(null);
  const holdPip = pipModeRef.current === 'element';
  const mainRef = useRef<string | null>(null);
  const mainId = pickMainRemote(
    remoteIds.filter((id) => state.remoteStreams[id]),
    holdPip ? new Set<string>() : speaking,
    mainRef.current
  );
  mainRef.current = mainId;
  const mainHasVideo = !!mainId && video && showsVideo(mainId);
  const mainStream =
    mainId && video && (mainHasVideo || holdPip)
      ? (state.remoteStreams[mainId] ?? null)
      : null;
  const pip = useCallPip({
    active: active && video,
    video: mainStream ? videoForStream(mainStream) : null,
    // Only a live call goes back to the tile (D268).
    onLeave: () => {
      if (statusRef.current === 'active') setMinimized(true);
    },
  });
  pipModeRef.current = pip.mode;
  // Keep this person's element mounted while it's in PiP (camera off).
  const keepMounted = (id: string) => pip.mode === 'element' && id === mainId;
  // The PiP window takes the videos; the app shows the tile meanwhile.
  useEffect(() => {
    if (pip.mode === 'document') setMinimized(true);
  }, [pip.mode, setMinimized]);
  // Back minimises the full call screen instead of leaving the page (D267).
  const fullOpen = active && !minimized;
  const closePicker = addPicker.onClose;
  useEffect(() => {
    if (!fullOpen) return;
    return holdBackForCall(() => {
      closePicker();
      setMinimized(true);
    });
  }, [fullOpen, setMinimized, closePicker]);

  if (state.status === 'idle') return null;

  const resolveName = (id: string) => {
    if (id === state.initiator?.id) return state.initiator.displayName;
    const member = currentFamily?.members.find((m) => m.user.id === id);
    return (
      member?.nickname ??
      member?.user.displayName ??
      participantNames[id]?.name ??
      t('chat.callScreen.participant')
    );
  };
  const resolveAvatar = (id: string) => {
    if (id === state.initiator?.id)
      return state.initiator.avatarUrl ?? undefined;
    const member = currentFamily?.members.find((m) => m.user.id === id);
    return (
      member?.user.avatarUrl ?? participantNames[id]?.avatarUrl ?? undefined
    );
  };

  // Voices play from one place that never moves (D267).
  const withAudio = (view: React.ReactNode) => (
    <>
      <CallAudio streams={state.remoteStreams} />
      {view}
      {pip.pipWindow &&
        createPortal(<CallPipView {...mini} />, pip.pipWindow.document.body)}
    </>
  );
  const mini: Omit<MiniCallProps, 'video'> = {
    title: info.title,
    clock,
    main: mainId
      ? {
          name: resolveName(mainId),
          avatarUrl: resolveAvatar(mainId),
          ownerId: mainId,
          familyId: callFamilyId,
          stream: mainStream,
          pictureOff: !mainHasVideo,
          reconnecting: isReconnecting(mainId),
        }
      : null,
    localStream: video && state.cameraEnabled ? state.localStream : null,
    mirrorLocal: state.facingMode === 'user',
    micEnabled: state.micEnabled,
    onToggleMic: toggleMic,
    onEnd: endCall,
  };
  const shell = (children: React.ReactNode, bg = CALL_BG) => (
    <DarkMode>
      <Box
        data-theme="dark"
        position="fixed"
        inset={0}
        h="var(--app-height, 100vh)"
        zIndex={2000}
        bg={bg}
        color="#F3F0EB"
        pt="env(safe-area-inset-top)"
      >
        {children}
      </Box>
    </DarkMode>
  );

  // Minimised: a floating tile over the app, which stays usable (D267).
  if (minimized && active) {
    return withAudio(
      <CallTile
        {...mini}
        video={video}
        inPictureInPicture={pip.mode === 'document'}
        videoFallback={state.videoFallback}
        onExpand={() => {
          // Out of PiP first, so the videos come back to the page (S1).
          if (pip.mode) pip.exit({ toFullScreen: true });
          setMinimized(false);
        }}
      />
    );
  }

  // ---- Ringing (incoming or outgoing) -------------------------------------
  if (state.status === 'ringing' || state.status === 'connecting') {
    const incoming = state.direction === 'incoming';
    const callerId = state.initiator?.id ?? '';
    // Incoming: the caller's face. Outgoing: who you are calling.
    const faceName = incoming
      ? resolveName(callerId)
      : info.title || t('chat.callScreen.calling');
    const subtitle = incoming
      ? isGroupCall && info.title
        ? t('chat.callScreen.isCallingGroup', { name: info.title })
        : t('chat.callScreen.isCallingYou')
      : state.status === 'connecting'
        ? t('chat.callScreen.connecting')
        : // Their phone said it's ringing (D278).
          state.ringing.length > 0
          ? t('chat.callScreen.ringingDots')
          : t('chat.callScreen.callingDots');

    return withAudio(
      shell(
        <Flex direction="column" align="center" h="full">
          <HStack
            spacing={2}
            pt={5}
            fontSize="15px"
            fontWeight={600}
            color="#D6D0C6"
          >
            <LogoMark
              size={20}
              color="#E58A94"
              light={CALL_BG}
              aria-hidden="true"
            />
            <Text>
              {t(
                video
                  ? 'chat.callScreen.videoTitle'
                  : 'chat.callScreen.voiceTitle'
              )}
            </Text>
          </HStack>
          <Flex
            flex="1"
            direction="column"
            align="center"
            justify="center"
            gap={5}
            pb={10}
            px={4}
          >
            <Flex
              aria-hidden="true"
              w="240px"
              h="240px"
              borderRadius="full"
              boxShadow="inset 0 0 0 1px rgba(240, 182, 63, 0.18)"
              align="center"
              justify="center"
            >
              <Flex
                w="196px"
                h="196px"
                borderRadius="full"
                boxShadow="inset 0 0 0 2px rgba(240, 182, 63, 0.35)"
                align="center"
                justify="center"
              >
                <Avatar
                  size="2xl"
                  name={faceName}
                  src={incoming ? resolveAvatar(callerId) : undefined}
                  ownerId={callerId}
                  familyId={callFamilyId}
                  boxShadow={`0 0 0 4px ${SAFFRON}`}
                  sx={{ '--avatar-font-size': '60px' }}
                  fontFamily="heading"
                  fontWeight={800}
                />
              </Flex>
            </Flex>
            <VStack spacing="6px" textAlign="center">
              <Text
                fontFamily="heading"
                fontWeight={800}
                fontSize="44px"
                letterSpacing="-0.02em"
                lineHeight={1}
              >
                {faceName}
              </Text>
              <Text fontSize="18px" color="#D6D0C6">
                {subtitle}
              </Text>
            </VStack>
          </Flex>
          <Grid
            w="full"
            maxW="420px"
            templateColumns={incoming ? 'repeat(2, minmax(0, 1fr))' : '1fr'}
            px={10}
            pb="max(env(safe-area-inset-bottom), 72px)"
          >
            <VStack spacing="10px">
              <IconButton
                colorScheme="gray"
                aria-label={t(
                  incoming
                    ? 'chat.callScreen.declineCall'
                    : 'chat.callScreen.cancelCall'
                )}
                icon={<HangUpIcon size={36} />}
                w="88px"
                h="88px"
                bg={DECLINE}
                color="white"
                _hover={{ bg: '#B02020' }}
                _active={{ bg: '#9E1C1C' }}
                onClick={incoming ? rejectCall : endCall}
              />
              <Text fontSize="18px" fontWeight={700}>
                {incoming ? t('chat.callScreen.decline') : t('common.cancel')}
              </Text>
            </VStack>
            {incoming && (
              <VStack spacing="10px">
                <IconButton
                  colorScheme="gray"
                  aria-label={t('chat.callScreen.answerCall')}
                  icon={video ? <VideoIcon size={36} /> : <Phone size={36} />}
                  w="88px"
                  h="88px"
                  bg={ANSWER}
                  color="white"
                  _hover={{ bg: '#166A3A' }}
                  _active={{ bg: '#125A31' }}
                  onClick={() => void acceptCall()}
                />
                <Text fontSize="18px" fontWeight={700}>
                  {t('chat.callScreen.answer')}
                </Text>
              </VStack>
            )}
          </Grid>
        </Flex>
      )
    );
  }

  const addButton = (bg: string) =>
    addable.length > 0 ? (
      <IconButton
        colorScheme="gray"
        aria-label={t('chat.callScreen.addSomeone')}
        icon={<UserPlus size={20} />}
        bg={bg}
        color="#F3F0EB"
        _hover={{ bg: '#34302C' }}
        onClick={addPicker.onOpen}
      />
    ) : null;

  // Ring someone from the chat (D38).
  const picker = (
    <Modal isOpen={addPicker.isOpen} onClose={addPicker.onClose} isCentered>
      {/* Above the call screen, which sits over everything (z-index 2000). */}
      <ModalOverlay bg="rgba(0, 0, 0, 0.6)" zIndex={2100} />
      <ModalContent
        containerProps={{ zIndex: 2100 }}
        data-theme="dark"
        mx={4}
        borderRadius="28px"
        bg={TILE_BG}
        color="#F3F0EB"
      >
        <ModalHeader fontFamily="heading" fontSize="22px">
          {t('chat.callScreen.addTitle')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          <VStack align="stretch" spacing={1}>
            {addable.map((id) => (
              <HStack key={id} spacing={3} py={2}>
                <Avatar
                  size="sm"
                  name={resolveName(id)}
                  src={resolveAvatar(id)}
                  ownerId={id}
                  familyId={callFamilyId}
                />
                <Text flex="1" fontWeight={700} noOfLines={1}>
                  {resolveName(id)}
                </Text>
                <Button
                  colorScheme="gray"
                  h="40px"
                  bg={ANSWER}
                  color="white"
                  _hover={{ bg: '#166A3A' }}
                  leftIcon={<Phone size={16} />}
                  onClick={() => {
                    ringMore([id]);
                    addPicker.onClose();
                    toast({
                      title: t('chat.callScreen.ringing', {
                        name: resolveName(id),
                      }),
                      status: 'info',
                      duration: 3000,
                    });
                  }}
                >
                  {t('chat.callScreen.ring')}
                </Button>
              </HStack>
            ))}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  const minimiseButton = (bg: string) => (
    <IconButton
      colorScheme="gray"
      aria-label={t('chat.callScreen.minimise')}
      icon={<ChevronDown size={22} />}
      bg={bg}
      color="#F3F0EB"
      _hover={{ bg: '#34302C' }}
      onClick={() => {
        addPicker.onClose();
        setMinimized(true);
      }}
    />
  );

  const pipButton =
    pip.supported && mainStream ? (
      <IconButton
        colorScheme="gray"
        aria-label={t('chat.callScreen.pictureInPicture')}
        icon={<PictureInPicture2 size={20} />}
        bg="rgba(15, 14, 13, 0.7)"
        color="#F3F0EB"
        _hover={{ bg: '#34302C' }}
        onClick={() => void pip.enter()}
      />
    ) : null;

  // ---- Active video call --------------------------------------------------
  if (video) {
    const tiles = remoteIds.length;
    return withAudio(
      shell(
        <Box position="relative" h="full">
          <Grid
            h="full"
            gap="4px"
            p="4px"
            // Tiles stop above the floating controls so names stay visible.
            pb="calc(max(env(safe-area-inset-bottom), 34px) + 92px)"
            templateColumns={tiles > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr'}
            templateRows={`repeat(${Math.max(1, Math.ceil(tiles / 2))}, minmax(0, 1fr))`}
          >
            {remoteIds.map((id, index) => {
              const stream = state.remoteStreams[id];
              const hasVideo =
                !remoteCameraOff(id) &&
                stream
                  ?.getVideoTracks()
                  .some((t) => t.enabled && t.readyState === 'live');
              const radius =
                tiles === 1
                  ? '28px'
                  : index === 0
                    ? '28px 8px 8px 8px'
                    : index === 1
                      ? '8px 28px 8px 8px'
                      : '8px';
              return (
                <Box
                  key={id}
                  position="relative"
                  overflow="hidden"
                  borderRadius={radius}
                  bg={TILE_BG}
                  gridColumn={
                    tiles % 2 === 1 && index === tiles - 1 && tiles > 1
                      ? 'span 2'
                      : undefined
                  }
                >
                  {stream && (hasVideo || keepMounted(id)) && (
                    <StreamVideo stream={stream} />
                  )}
                  {!(stream && hasVideo) && (
                    <Flex
                      position="absolute"
                      inset={0}
                      bg={TILE_BG}
                      direction="column"
                      align="center"
                      justify="center"
                      gap={2}
                    >
                      <Avatar
                        size="xl"
                        name={resolveName(id)}
                        src={resolveAvatar(id)}
                        ownerId={id}
                        familyId={callFamilyId}
                      />
                      <HStack spacing={1} fontSize="13px" color="#ABA49A">
                        {stream ? (
                          <VideoOff size={14} aria-hidden="true" />
                        ) : null}
                        <Text>
                          {stream
                            ? t('chat.callScreen.cameraOff')
                            : t('chat.callScreen.connecting')}
                        </Text>
                      </HStack>
                    </Flex>
                  )}
                  {speaking.has(id) && (
                    <Box
                      position="absolute"
                      inset={0}
                      borderRadius="inherit"
                      boxShadow={`inset 0 0 0 4px ${SAFFRON}`}
                      pointerEvents="none"
                    />
                  )}
                  {isReconnecting(id) && (
                    <Flex
                      position="absolute"
                      inset={0}
                      bg="rgba(15, 14, 13, 0.55)"
                      direction="column"
                      align="center"
                      justify="center"
                      gap="10px"
                      role="status"
                    >
                      <Spinner
                        color={SAFFRON}
                        emptyColor="#3A3632"
                        thickness="3px"
                        size="lg"
                      />
                      <Text fontSize="14px" fontWeight={600}>
                        {t('chat.callScreen.reconnecting')}
                      </Text>
                    </Flex>
                  )}
                  <Text
                    position="absolute"
                    left="10px"
                    bottom="10px"
                    h="28px"
                    px="10px"
                    borderRadius="14px"
                    bg="rgba(15, 14, 13, 0.75)"
                    fontSize="14px"
                    fontWeight={700}
                    display="flex"
                    alignItems="center"
                    gap="6px"
                  >
                    {resolveName(id)}
                    {remoteMuted(id) && (
                      <MicOff
                        size={14}
                        color="#E58A94"
                        role="img"
                        aria-label={t('chat.callScreen.muted')}
                      />
                    )}
                  </Text>
                </Box>
              );
            })}
            {tiles === 0 && (
              <Flex
                align="center"
                justify="center"
                direction="column"
                gap={3}
                role="status"
              >
                <Box
                  aria-hidden="true"
                  w="32px"
                  h="32px"
                  borderRadius="full"
                  border="3px solid #3A3632"
                  borderTopColor={SAFFRON}
                  animation="spin 0.9s linear infinite"
                  sx={{
                    '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
                  }}
                />
                <Text fontSize="14px" fontWeight={600}>
                  {t('chat.callScreen.connecting')}
                </Text>
              </Flex>
            )}
          </Grid>

          <HStack position="absolute" top={3} left={3} spacing={2}>
            {minimiseButton('rgba(15, 14, 13, 0.7)')}
            {addButton('rgba(15, 14, 13, 0.7)')}
            {pipButton}
            <Text
              h="32px"
              px={3}
              borderRadius="16px"
              bg="rgba(15, 14, 13, 0.7)"
              fontSize="14px"
              fontWeight={700}
              display="flex"
              alignItems="center"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {info.title ? `${info.title} · ${clock}` : clock}
            </Text>
          </HStack>

          {/* Weak connection: we paused video, or it has recovered (D225). */}
          {state.videoFallback && (
            <HStack
              position="absolute"
              top="52px"
              left={3}
              right={3}
              mx="auto"
              w="fit-content"
              maxW="calc(100% - 24px)"
              spacing={2}
              pl={3}
              pr={1}
              py={1}
              borderRadius="18px"
              bg="rgba(15, 14, 13, 0.85)"
              boxShadow="0 0 0 1px #3A3632"
              role="status"
            >
              <WifiOff size={16} aria-hidden="true" color={SAFFRON} />
              <Text fontSize="13px" fontWeight={600}>
                {t(
                  state.videoFallback === 'paused'
                    ? 'chat.callScreen.weakVideoPaused'
                    : 'chat.callScreen.videoBetter'
                )}
              </Text>
              <Button
                size="sm"
                h="28px"
                borderRadius="14px"
                bg="#F3F0EB"
                color={CALL_BG}
                _hover={{ opacity: 0.9 }}
                flexShrink={0}
                onClick={resumeVideo}
              >
                {t('chat.callScreen.turnVideoOn')}
              </Button>
              {state.videoFallback === 'better' && (
                <IconButton
                  size="sm"
                  h="28px"
                  minW="28px"
                  variant="ghost"
                  colorScheme="gray"
                  borderRadius="full"
                  aria-label={t('common.close')}
                  icon={<X size={16} />}
                  onClick={dismissVideoHint}
                />
              )}
            </HStack>
          )}

          {/* You, in the corner above the controls. */}
          <Box
            position="absolute"
            right={4}
            bottom="calc(max(env(safe-area-inset-bottom), 34px) + 104px)"
            w={{ base: '100px', md: '160px' }}
            h={{ base: '140px', md: '120px' }}
            borderRadius="18px"
            overflow="hidden"
            bg="#4A3A30"
            boxShadow={`0 0 0 2px ${VIDEO_BG}, 0 8px 20px rgba(0, 0, 0, 0.5)`}
          >
            {state.localStream && state.cameraEnabled ? (
              <StreamVideo
                stream={state.localStream}
                mirror={state.facingMode === 'user'}
              />
            ) : (
              <Flex h="full" align="center" justify="center" color="#ABA49A">
                <VideoOff
                  size={22}
                  aria-label={t('chat.callScreen.yourCameraOff')}
                />
              </Flex>
            )}
            <Text
              position="absolute"
              bottom="8px"
              w="full"
              textAlign="center"
              fontSize="13px"
              fontWeight={700}
              textShadow="0 1px 2px rgba(0,0,0,0.6)"
            >
              {t('common.you')}
            </Text>
          </Box>

          <HStack
            position="absolute"
            left={4}
            right={4}
            bottom="max(env(safe-area-inset-bottom), 34px)"
            mx="auto"
            maxW="420px"
            justify="space-between"
            px="14px"
            py="10px"
            borderRadius="40px"
            bg="rgba(23, 21, 19, 0.88)"
            boxShadow="0 0 0 1px #3A3632"
          >
            <IconButton
              colorScheme="gray"
              aria-label={t(
                state.micEnabled
                  ? 'chat.callScreen.muteMic'
                  : 'chat.callScreen.unmuteMic'
              )}
              aria-pressed={!state.micEnabled}
              icon={state.micEnabled ? <Mic size={26} /> : <MicOff size={26} />}
              w="60px"
              h="60px"
              bg={state.micEnabled ? CONTROL_BG : '#F3F0EB'}
              color={state.micEnabled ? '#F3F0EB' : CALL_BG}
              _hover={{ opacity: 0.9 }}
              onClick={toggleMic}
            />
            <IconButton
              colorScheme="gray"
              aria-label={t(
                state.cameraEnabled
                  ? 'chat.callScreen.cameraOffAction'
                  : 'chat.callScreen.cameraOnAction'
              )}
              aria-pressed={!state.cameraEnabled}
              icon={
                state.cameraEnabled ? (
                  <VideoIcon size={26} />
                ) : (
                  <VideoOff size={26} />
                )
              }
              w="60px"
              h="60px"
              bg={state.cameraEnabled ? CONTROL_BG : '#F3F0EB'}
              color={state.cameraEnabled ? '#F3F0EB' : CALL_BG}
              _hover={{ opacity: 0.9 }}
              onClick={toggleCamera}
            />
            {state.canFlipCamera && (
              <IconButton
                colorScheme="gray"
                aria-label={t('chat.callScreen.flip')}
                icon={<SwitchCamera size={26} />}
                w="60px"
                h="60px"
                bg={CONTROL_BG}
                color="#F3F0EB"
                _hover={{ opacity: 0.9 }}
                onClick={() => void flipCamera()}
              />
            )}
            <IconButton
              colorScheme="gray"
              aria-label={t('chat.callScreen.end')}
              icon={<HangUpIcon size={28} />}
              w="84px"
              h="60px"
              bg={DECLINE}
              color="white"
              _hover={{ bg: '#B02020' }}
              onClick={endCall}
            />
          </HStack>
          {picker}
        </Box>,
        VIDEO_BG
      )
    );
  }

  // ---- Active voice call --------------------------------------------------
  const people = [...remoteIds, ...(currentUser ? [currentUser.id] : [])];
  return withAudio(
    shell(
      <Flex direction="column" h="full" maxW="560px" mx="auto">
        <HStack h="60px" px={2} spacing={2} flexShrink={0}>
          {minimiseButton(CONTROL_BG)}
          <VStack flex="1" spacing={0}>
            <Text
              fontFamily="heading"
              fontWeight={700}
              fontSize="19px"
              noOfLines={1}
            >
              {info.title || t('chat.callScreen.call')}
            </Text>
            <Text
              fontSize="14px"
              color="#D6D0C6"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {t('chat.callScreen.voiceClock', { clock })}
            </Text>
          </VStack>
          {/* Keeps the title centred opposite the minimise button. */}
          {addButton(CONTROL_BG) ?? <Box w="40px" />}
        </HStack>

        <Grid
          flex="1"
          minH={0}
          overflowY="auto"
          templateColumns="repeat(2, minmax(0, 1fr))"
          gap={3}
          px={5}
          py={4}
          alignContent="center"
        >
          {people.map((id) => {
            const self = isSelf(id);
            const stream = state.remoteStreams[id];
            const talking = speaking.has(id);
            const status = self
              ? state.micEnabled
                ? ' '
                : t('chat.callScreen.muted')
              : isReconnecting(id)
                ? t('chat.callScreen.reconnecting')
                : !stream
                  ? t('chat.callScreen.connecting')
                  : talking
                    ? t('chat.callScreen.speaking')
                    : remoteMuted(id)
                      ? t('chat.callScreen.muted')
                      : ' ';
            return (
              <VStack
                key={id}
                spacing="10px"
                py="14px"
                borderRadius="24px"
                bg={self ? CONTROL_BG : TILE_BG}
                boxShadow={self ? 'inset 0 0 0 1px #3A3632' : undefined}
              >
                {self ? (
                  <Flex
                    w="96px"
                    h="96px"
                    borderRadius="full"
                    bg="#9A2A38"
                    color="white"
                    align="center"
                    justify="center"
                    fontSize="26px"
                    fontWeight={700}
                    boxShadow={
                      talking
                        ? `0 0 0 4px ${CALL_BG}, 0 0 0 7px ${SAFFRON}`
                        : undefined
                    }
                  >
                    {t('common.you')}
                  </Flex>
                ) : (
                  <Avatar
                    size="xl"
                    name={resolveName(id)}
                    src={resolveAvatar(id)}
                    ownerId={id}
                    familyId={callFamilyId}
                    boxShadow={
                      talking
                        ? `0 0 0 4px ${CALL_BG}, 0 0 0 7px ${SAFFRON}`
                        : undefined
                    }
                  />
                )}
                <HStack
                  spacing="6px"
                  fontSize="17px"
                  fontWeight={700}
                  maxW="full"
                  px={2}
                >
                  <Text noOfLines={1}>
                    {self ? t('common.you') : resolveName(id)}
                  </Text>
                  {(self ? !state.micEnabled : remoteMuted(id)) && (
                    <MicOff
                      size={16}
                      color="#E58A94"
                      role="img"
                      aria-label={t('chat.callScreen.muted')}
                    />
                  )}
                </HStack>
                <Text
                  fontSize="13px"
                  mt="-6px"
                  fontWeight={talking ? 700 : 400}
                  color={talking ? SAFFRON : '#ABA49A'}
                  whiteSpace="pre"
                >
                  {status}
                </Text>
              </VStack>
            );
          })}
        </Grid>

        <VStack
          spacing={4}
          align="stretch"
          px={5}
          pt={1}
          pb="max(env(safe-area-inset-bottom), 42px)"
          flexShrink={0}
        >
          <Flex justify="center">
            <RoundControl
              label={t(
                state.micEnabled
                  ? 'chat.callScreen.muteMic'
                  : 'chat.callScreen.unmuteMic'
              )}
              caption={t(
                state.micEnabled
                  ? 'chat.callScreen.mute'
                  : 'chat.callScreen.unmute'
              )}
              icon={state.micEnabled ? <Mic size={28} /> : <MicOff size={28} />}
              active={!state.micEnabled}
              onClick={toggleMic}
            />
          </Flex>
          <Button
            colorScheme="gray"
            h="64px"
            borderRadius="32px"
            bg={DECLINE}
            color="white"
            fontSize="19px"
            _hover={{ bg: '#B02020' }}
            _active={{ bg: '#9E1C1C' }}
            leftIcon={<HangUpIcon size={28} />}
            onClick={endCall}
          >
            {t('chat.callScreen.end')}
          </Button>
        </VStack>
        {picker}
      </Flex>
    )
  );
}
